import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Box, Container, Grid2 as Grid, List, ListItem, Link as MuiLink, Typography } from '@mui/material';
import { map } from 'lodash';
import { Fragment, ReactNode, useEffect, useLayoutEffect } from 'react';
import PageTitle from '../components/coreLayout/PageTitle';
import Seo from '../components/coreLayout/Seo';

const termsSx = {
    containerPadding: {
        paddingLeft: { xs: '10px', sm: '24px' },
        paddingRight: { xs: '10px', sm: '24px' },
    },
    listContainer: {
        margin: '10px auto 60px',
        maxWidth: 900,
    },
    section: {
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
        marginBottom: '16px',
        padding: { xs: '14px 14px', sm: '18px 22px' },
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '6px',
    },
    iconWrap: {
        display: 'flex',
        color: 'primary.main',
    },
    sectionTitle: {
        fontWeight: 500,
        fontSize: { xs: 16, sm: 18 },
    },
    term: {
        alignItems: 'flex-start',
        padding: '4px 0',
        opacity: 0.85,
    },
    bullet: {
        flexShrink: 0,
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: 'primary.main',
        marginRight: '14px',
        marginTop: '7px',
    },
    termText: {
        fontSize: 16,
        lineHeight: '24px',
    },
} as const;

interface TermsGroup {
    title: string;
    icon: JSX.Element;
    items: ReactNode[];
}

export default function TermsAndConditions() {
    useEffect(() => {
        const description = document.querySelector('meta[name="description"]');
        if (description) {
            description.remove();
        }
    }, []);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const termsGroups: TermsGroup[] = [
        {
            title: 'Pricing & Payment',
            icon: <PaymentsOutlinedIcon />,
            items: [
                'Prices listed on this site are per person, and include GST.',
                'Tours and pricing are subject to change without notice.',
                'American Express and Diners Club cards are not accepted.',
            ],
        },
        {
            title: 'Bookings, Cancellations & No-shows',
            icon: <EventBusyOutlinedIcon />,
            items: [
                'A 100% cancellation fee applies for cancellations, no shows, or booking changes within 24 hours of departure.',
                'Passengers that do not arrive on time for check-in, including scheduled bus pick-ups, may be classed as a no-show. Please allow extra time for parking (if applicable), and plan to arrive 10 minutes before your scheduled check-in time.',
                'Bus transfers are subject to availability, and GSL will advise you if this service is not available.',
                'Some tours are not suitable for cruise ship guests, or guests of island resorts.',
                'Cruise ship passengers are responsible for checking tour times prior to booking, and ensuring that tender times will suit; GSL will not honor refunds or cancellations within 24 hours, if guests cannot travel because of tender time restrictions.',
                'GSL will not honor refunds or cancellations within 24 hours, for guests that are staying at Island Resorts, and cannot make their tour because of ferry time restrictions.',
                'Travel agents shall ensure terms and conditions are communicated with clients.',
            ],
        },
        {
            title: 'Weather & Operations',
            icon: <CloudQueueOutlinedIcon />,
            items: [
                'All flights are subject to pilot discretion, weather, air traffic control and aircraft availability.',
                'Minimum numbers apply for tours to proceed.',
                'Flight time listed is approximate and may vary depending on aircraft type.',
                'Tours, or portions of tours, may be cancelled by adverse weather conditions or engineering reasons. GSL will endeavour to reschedule tours, accommodate guests on other tours, or provide alternative options. In the event that guests are not able to travel at all, the fare will be refunded.',
                'GSL Aviation will endeavour to achieve on-time performance, but accept no responsibility for missing tour/flight connections as a result of operational delays.',
                <Fragment key="fleet-centr">
                    GSL Aviation uses aviation compliance and operations tools provided by{' '}
                    <MuiLink
                        href="https://fleetcentr.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ color: 'primary.main' }}
                    >
                        Fleet Centr
                    </MuiLink>
                    .
                </Fragment>,
            ],
        },
        {
            title: 'Partner Providers & Packages',
            icon: <HandshakeOutlinedIcon />,
            items: [
                'GSL Aviation does not operate aviation tour products in the Whitsundays. Products sold on this site are booked with partner providers, and subject to their terms and conditions.',
                'Package products booked with GSL Aviation, are also subject to the terms and conditions of the partner providers.',
                'Should any one part of a package tour not operate - you will be charged the current retail price of the tour component(s) completed.',
            ],
        },
        {
            title: 'Safety, Insurance & Liability',
            icon: <HealthAndSafetyOutlinedIcon />,
            items: [
                'Weight restrictions apply on all flight products.',
                'All tours are covered by carriers liability insurance, specific to the prescribed activities being undertaken; those policies may not extend to claims where guests are found to be negligent, such as not following directions of crew.',
                'GSL highly recommend that all guests have their own travel insurance, to cover losses such as lost/damaged personal items on tour, injuries caused by negligence, tour delays, illness, and other such scenarios which may cause financial or personal loss outside of prescribed liabilities under company policies. GSL Aviation will not provide compensation for losses that are not covered under our relevant policies, such as lost income, or costs incurred by changing travel plans.',
            ],
        },
        {
            title: 'Privacy & Media',
            icon: <CameraAltOutlinedIcon />,
            items: [
                'GSL Aviation may use photographs of our clients in promotional material, including but not limited to social media. If you do not consent please advise GSL Aviation.',
            ],
        },
    ];

    return (
        <div>
            <Seo
                title="Terms & Conditions | GSL Aviation Cairns"
                description="Terms and conditions for booking scenic flights, Great Barrier Reef tours and air charter with GSL Aviation in Cairns."
                path="/termsandconditions"
            />
            <Container maxWidth="lg" sx={termsSx.containerPadding}>
                <PageTitle title="Terms & Conditions" tagline="A few things to know for your trip with GSL" compact />
            </Container>
            <Container maxWidth="md" sx={termsSx.containerPadding}>
                <Box sx={termsSx.listContainer}>
                    <Grid container>
                        <Grid size={{ xs: 12 }}>
                            {map(termsGroups, (group, gi) => (
                                <Box key={`group${gi}`} sx={termsSx.section}>
                                    <Box sx={termsSx.sectionHeader}>
                                        <Box component="span" sx={termsSx.iconWrap}>
                                            {group.icon}
                                        </Box>
                                        <Typography sx={termsSx.sectionTitle}>{group.title}</Typography>
                                    </Box>
                                    <List disablePadding>
                                        {map(group.items, (term, ti) => (
                                            <ListItem key={`group${gi}item${ti}`} sx={termsSx.term} disableGutters>
                                                <Box component="span" sx={termsSx.bullet} />
                                                <Typography sx={termsSx.termText}>{term}</Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </div>
    );
}
